import { useCallback, useState, type ReactNode } from 'react';
import type { KioskConfigDTO, KioskUnavailableReason } from '@print-saas/shared';
import { PinDialog } from '../components/PinDialog';
import { PrinterPicker } from '../components/PrinterPicker';
import { AlertIcon, KeyIcon, PaperIcon, PrinterIcon, WifiOffIcon } from '../components/icons';
import { MessageScreen } from '../components/MessageScreen';
import { Spinner } from '../components/Spinner';

export function BootScreen({ label = 'Starting up…' }: { label?: string }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-[3vmin] text-muted">
      <Spinner className="size-[10vmin] text-accent" />
      <p className="text-[3.4vmin] font-semibold">{label}</p>
    </div>
  );
}

export function ReconnectingScreen() {
  return (
    <MessageScreen
      tone="warn"
      icon={<WifiOffIcon />}
      title="Reconnecting…"
      subtitle="This kiosk lost its connection. It will be back in a moment."
    >
      <Spinner className="size-[7vmin] text-warn" />
    </MessageScreen>
  );
}

const unavailableCopy: Record<KioskUnavailableReason, { title: string; subtitle: string; icon: ReactNode }> = {
  DISABLED: {
    title: 'Temporarily out of service',
    subtitle: 'Sorry for the inconvenience. Please try again later.',
    icon: <AlertIcon />,
  },
  OUT_OF_PAPER: {
    title: 'Out of paper — we’ll be back soon',
    subtitle: 'Staff have been notified and will refill the printer shortly.',
    icon: <PaperIcon />,
  },
  NO_PRINTER: {
    title: 'Choose a printer',
    subtitle: 'Select the printer connected to this kiosk.',
    icon: <PrinterIcon />,
  },
  PRINTER_OFFLINE: {
    title: 'Printer offline',
    subtitle: 'We’re working on it. Please try again in a few minutes.',
    icon: <PrinterIcon />,
  },
  PRINTER_ERROR: {
    title: 'Printer needs attention',
    subtitle: 'Staff have been notified. Please try again in a few minutes.',
    icon: <PrinterIcon />,
  },
};

export function UnavailableScreen({ reason }: { reason: KioskUnavailableReason | null }) {
  // A reason this screen doesn't know yet (a newer server) still shows something sensible.
  const copy = unavailableCopy[reason ?? 'DISABLED'] ?? unavailableCopy.DISABLED;
  return <MessageScreen tone="warn" icon={copy.icon} title={copy.title} subtitle={copy.subtitle} />;
}

/** First run (or the chosen printer is gone): pick one of the printers installed on this computer. */
export function NoPrinterScreen({
  config,
  onSelect,
  readOnly,
}: {
  config: KioskConfigDTO;
  onSelect: (name: string, pin?: string) => Promise<void>;
  readOnly: boolean;
}) {
  const copy = unavailableCopy.NO_PRINTER;
  // With a kiosk PIN set, choosing a printer asks for it first.
  const [pending, setPending] = useState<string | null>(null);
  const choose = async (name: string) => {
    if (config.pinRequired) setPending(name);
    else await onSelect(name);
  };
  const cancelPin = useCallback(() => setPending(null), []);
  return (
    <MessageScreen tone="accent" icon={copy.icon} title={copy.title} subtitle={copy.subtitle}>
      <div className="w-[min(100vmin,92vw)]">
        <PrinterPicker printer={config.printer} onSelect={choose} readOnly={readOnly} />
      </div>
      <PinDialog
        open={pending !== null}
        title="Set this printer?"
        onSubmit={async (pin) => {
          if (pending) await onSelect(pending, pin);
          setPending(null);
        }}
        onCancel={cancelPin}
      />
    </MessageScreen>
  );
}

const DISCONNECTED_TITLE = 'This kiosk was disconnected from its portal';
const PAIR_AGAIN = 'On this PC, open ‘Pair this kiosk again’ and enter a new code from the portal (Kiosk → Set up this kiosk).';

/** The server refused this screen's kiosk key (rotated, paired again elsewhere, or the kiosk was deleted). */
export function InvalidKeyScreen({ message, onRepair }: { message: string | null; onRepair: () => void }) {
  return (
    <MessageScreen
      tone="bad"
      icon={<KeyIcon />}
      title={DISCONNECTED_TITLE}
      subtitle={PAIR_AGAIN}
      footer={
        <div className="flex flex-col items-center gap-3">
          {message && <code className="text-[1.9vmin] text-muted/70">{message}</code>}
          <button
            type="button"
            onClick={onRepair}
            className="mt-[1vmin] rounded-[1.8vmin] bg-white/8 px-[5vmin] py-[2vmin] text-[2.6vmin] font-semibold ring-1 ring-line active:bg-white/15"
          >
            Re-pair this screen
          </button>
        </div>
      }
    />
  );
}

/**
 * This screen is still connected, but the kiosk agent on the PC was cut off by Rotate key and hasn't been paired again:
 * the kiosk can't print, so say what to do instead of "choose a printer" or "printer offline".
 */
export function AgentNeedsPairingScreen() {
  return <MessageScreen tone="bad" icon={<KeyIcon />} title={DISCONNECTED_TITLE} subtitle={PAIR_AGAIN} />;
}

/** This kiosk was made a shop counter in the portal: a counter has a QR sticker and no screen. */
export function CounterModeScreen() {
  return (
    <MessageScreen
      tone="muted"
      icon={<PrinterIcon />}
      title="This is now a shop counter"
      subtitle="Customers scan the counter's QR sticker instead of this screen, so it isn't needed. Print the sticker from the counter's page in the portal; jobs still print here."
    />
  );
}

/** Owner preview whose link expired or was for a deleted kiosk. */
export function PreviewExpiredScreen() {
  return (
    <MessageScreen
      tone="muted"
      icon={<KeyIcon />}
      title="This preview link has expired"
      subtitle="Open the kiosk screen again from the kiosk's page in the owner dashboard."
    />
  );
}

/** Owner preview while the kiosk isn't showing a QR (its display is off or still starting). */
export function PreviewIdleScreen() {
  return (
    <MessageScreen
      tone="muted"
      icon={<WifiOffIcon />}
      title="The kiosk screen isn't showing a QR code right now"
      subtitle="Its display may be switched off or disconnected. This view updates as soon as it comes back."
    />
  );
}
